import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  scraperSourcesApi,
  scraperTriggersApi,
  scraperSchedulesApi,
  scraperStatsApi,
  scraperCaseStatsApi,
  type ScraperCaseStats,
} from '../services/scraperApi';
import API from '../services/api';
import type {
  ScraperSource,
  ScraperSourceType,
  ScraperSourceCategory,
  ScraperTrigger,
  ScraperSchedule,
  ScraperStats,
} from '../types/scraper';
import { formatDistanceToNow, format } from 'date-fns';
import { useToast } from '../contexts/ToastContext';
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  ArrowPathIcon,
  ShieldCheckIcon,
  Cog6ToothIcon,
  DocumentDuplicateIcon,
} from '@heroicons/react/24/outline';
import ReviewQueueTab from '../components/ReviewQueueTab';
import DuplicatesTab from '../components/DuplicatesTab';

type TopLevel = 'validation' | 'duplicates' | 'config';
type ConfigTab = 'sources' | 'runs';

const typeLabels: Record<ScraperSourceType, string> = {
  FACEBOOK: 'Facebook',
  TWITTER: 'Twitter / X',
  HTML: 'HTML',
  RSS: 'RSS Feed',
};

const categoryLabels: Record<ScraperSourceCategory, string> = {
  GOVERNMENT: 'Government',
  NGO: 'NGO',
  NEWS_OUTLET: 'News Outlet',
  COMMUNITY: 'Community',
  INTERNATIONAL_ORG: 'International Org',
  INDIGENOUS_ORG: 'Indigenous Org',
  HUMAN_RIGHTS_ORG: 'Human Rights Org',
  INDIGENOUS_MEDIA: 'Indigenous Media',
  NETWORK_COALITION: 'Network / Coalition',
};

const tierLabels: Record<number, { label: string; color: string }> = {
  1: { label: 'Every 4hr', color: 'bg-red-100 text-red-700' },
  2: { label: 'Every 8hr', color: 'bg-yellow-100 text-yellow-700' },
  3: { label: 'Every 24hr', color: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300' },
  4: { label: 'Twice a week', color: 'bg-blue-100 text-blue-700' },
  5: { label: 'Once a week', color: 'bg-slate-100 text-slate-700' },
};

const statusIcons: Record<string, React.ReactNode> = {
  OK: <CheckCircleIcon className="h-4 w-4 text-green-500" />,
  ERROR: <XCircleIcon className="h-4 w-4 text-red-500" />,
  BLOCKED: <ExclamationTriangleIcon className="h-4 w-4 text-orange-500" />,
};

interface FormState {
  open: boolean;
  editing: ScraperSource | null;
  type: ScraperSourceType;
  name: string;
  url: string;
  category: ScraperSourceCategory;
  tier: 1 | 2 | 3 | 4 | 5;
  maxItems: number;
  keywords: string;
  country: string;
  enabled: boolean;
}

const emptyForm: FormState = {
  open: false,
  editing: null,
  type: 'HTML',
  name: '',
  url: '',
  category: 'NEWS_OUTLET',
  tier: 2,
  maxItems: 5,
  keywords: '',
  country: '',
  enabled: true,
};

export default function Scraper() {
  const [topLevel, setTopLevel] = useState<TopLevel>('validation');
  const [configTab, setConfigTab] = useState<ConfigTab>('sources');
  const [form, setForm] = useState<FormState>(emptyForm);
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [enabledFilter, setEnabledFilter] = useState<string>('');
  const [regionFilter, setRegionFilter] = useState<string>('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const [triggerTier, setTriggerTier] = useState<string>('');
  const [triggerSourceType, setTriggerSourceType] = useState<string>('');

  // Stats
  const { data: stats } = useQuery<ScraperStats>({
    queryKey: ['scraper-stats'],
    queryFn: () => scraperStatsApi.get(),
  });

  // Case status counts (same source as the Needs Review sub-tab, so the KPI can't disagree with it).
  const { data: caseStats } = useQuery<ScraperCaseStats>({
    queryKey: ['scraper-case-stats'],
    queryFn: () => scraperCaseStatsApi.get(),
  });
  // Open duplicate matches awaiting a confirm/dismiss decision (the Needs Review dedup queue).
  const { data: pendingMatches } = useQuery<Array<{ id: string }>>({
    queryKey: ['dedup-pending-matches'],
    queryFn: async () => (await API.get('/api/dedup/pending-matches')).data,
  });

  // Sources tab queries
  const {
    data: scraperSources,
    isLoading: sourcesLoading,
    error: sourcesError,
  } = useQuery<ScraperSource[]>({
    queryKey: ['scraper-sources', typeFilter, categoryFilter, enabledFilter, regionFilter],
    queryFn: () => {
      const filters: { type?: ScraperSourceType; category?: ScraperSourceCategory; enabled?: boolean; region?: string } = {};
      if (typeFilter) filters.type = typeFilter as ScraperSourceType;
      if (categoryFilter) filters.category = categoryFilter as ScraperSourceCategory;
      if (enabledFilter) filters.enabled = enabledFilter === 'true';
      if (regionFilter) filters.region = regionFilter;
      return scraperSourcesApi.getAll(Object.keys(filters).length > 0 ? filters : undefined);
    },
    enabled: topLevel === 'config' && configTab === 'sources',
  });

  // Runs tab queries
  const {
    data: triggers,
    isLoading: triggersLoading,
    error: triggersError,
  } = useQuery<ScraperTrigger[]>({
    queryKey: ['scraper-triggers'],
    queryFn: () => scraperTriggersApi.getRecent(),
    refetchInterval: 15000,
    enabled: topLevel === 'config' && configTab === 'runs',
  });

  const { data: schedules } = useQuery<ScraperSchedule[]>({
    queryKey: ['scraper-schedules'],
    queryFn: () => scraperSchedulesApi.getAll(),
    enabled: topLevel === 'config' && configTab === 'runs',
  });

  // Schedule mutations
  const scheduleMutation = useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: { intervalHours?: number; runAtHour?: number; enabled?: boolean };
    }) => scraperSchedulesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scraper-schedules'] });
      showToast('success', 'Schedule updated');
    },
    onError: () => showToast('error', 'Failed to update schedule'),
  });

  // Trigger mutation
  const triggerMutation = useMutation({
    mutationFn: (data: { tier?: number; sourceType?: ScraperSourceType }) =>
      scraperTriggersApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scraper-triggers'] });
      queryClient.invalidateQueries({ queryKey: ['scraper-stats'] });
      showToast('success', 'Scrape run queued');
    },
    onError: () => showToast('error', 'Failed to trigger scraper'),
  });

  // Source mutations
  const createMutation = useMutation({
    mutationFn: (data: Partial<ScraperSource>) => scraperSourcesApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scraper-sources'] });
      setForm(emptyForm);
      showToast('success', 'Source created');
    },
    onError: () => showToast('error', 'Failed to create source'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ScraperSource> }) =>
      scraperSourcesApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scraper-sources'] });
      setForm(emptyForm);
      showToast('success', 'Source updated');
    },
    onError: () => showToast('error', 'Failed to update source'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => scraperSourcesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scraper-sources'] });
      setDeleteConfirm(null);
      showToast('success', 'Source deleted');
    },
    onError: () => showToast('error', 'Failed to delete source'),
  });

  const toggleEnabled = (source: ScraperSource) => {
    updateMutation.mutate({ id: source.id, data: { enabled: !source.enabled } });
  };

  const openCreate = () => {
    setForm({ ...emptyForm, open: true });
  };

  const openEdit = (source: ScraperSource) => {
    setForm({
      open: true,
      editing: source,
      type: source.type,
      name: source.name,
      url: source.url,
      category: source.category,
      tier: source.tier,
      maxItems: source.maxItems,
      keywords: source.keywords ? source.keywords.join(', ') : '',
      country: source.country || '',
      enabled: source.enabled,
    });
  };

  const handleSubmit = () => {
    const keywordsArray = form.keywords
      .split(',')
      .map((k) => k.trim())
      .filter(Boolean);

    const data: Partial<ScraperSource> = {
      type: form.type,
      name: form.name,
      url: form.url,
      category: form.category,
      tier: form.tier,
      maxItems: form.maxItems,
      keywords: keywordsArray.length > 0 ? keywordsArray : null,
      country: form.country || null,
      enabled: form.enabled,
    };

    if (form.editing) {
      updateMutation.mutate({ id: form.editing.id, data });
    } else {
      createMutation.mutate(data);
    }
  };

  const isLoading = topLevel === 'config' && configTab === 'sources' ? sourcesLoading : topLevel === 'config' && configTab === 'runs' ? triggersLoading : false;
  const error = topLevel === 'config' && configTab === 'sources' ? sourcesError : topLevel === 'config' && configTab === 'runs' ? triggersError : null;

  return (
    <div>
      {/* Form Modal */}
      {form.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
              {form.editing ? 'Edit Source' : 'Add Source'}
            </h3>

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Type</label>
                  <select
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value as ScraperSourceType })}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
                  >
                    {Object.entries(typeLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
                  <select
                    value={form.category}
                    onChange={(e) =>
                      setForm({ ...form, category: e.target.value as ScraperSourceCategory })
                    }
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
                  >
                    {Object.entries(categoryLabels).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Reuters Indigenous Rights"
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">URL</label>
                <input
                  type="text"
                  value={form.url}
                  onChange={(e) => setForm({ ...form, url: e.target.value })}
                  placeholder="https://example.com/feed"
                  className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Country</label>
                  <input
                    type="text"
                    value={form.country}
                    onChange={(e) => setForm({ ...form, country: e.target.value })}
                    placeholder="e.g., Philippines"
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Keywords <span className="text-gray-400 dark:text-gray-500 font-normal">(comma-separated)</span>
                  </label>
                  <input
                    type="text"
                    value={form.keywords}
                    onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                    placeholder="indigenous, land rights, FPIC"
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
                  />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tier</label>
                  <select
                    value={form.tier}
                    onChange={(e) =>
                      setForm({ ...form, tier: parseInt(e.target.value, 10) as 1 | 2 | 3 | 4 | 5 })
                    }
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
                  >
                    <option value={1}>Tier 1 (4hr)</option>
                    <option value={2}>Tier 2 (8hr)</option>
                    <option value={3}>Tier 3 (24hr)</option>
                    <option value={4}>Tier 4 (twice a week)</option>
                    <option value={5}>Tier 5 (once a week)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Max Items</label>
                  <input
                    type="number"
                    value={form.maxItems}
                    onChange={(e) =>
                      setForm({ ...form, maxItems: parseInt(e.target.value, 10) || 5 })
                    }
                    min={1}
                    max={50}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
                  />
                </div>
                <div className="flex items-end pb-1">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={form.enabled}
                      onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
                      className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-primary"
                    />
                    <span className="text-sm text-gray-700 dark:text-gray-300">Enabled</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-3">
              <button
                onClick={() => setForm(emptyForm)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700/40"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={
                  !form.name.trim() ||
                  !form.url.trim() ||
                  createMutation.isPending ||
                  updateMutation.isPending
                }
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 disabled:opacity-50"
              >
                {form.editing ? 'Save Changes' : 'Add Source'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteConfirm !== null && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-sm w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Delete Source</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Are you sure? This will permanently remove this scraper source.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700/40"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirm)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">AI Data Collection</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Review AI-validated cases, check for duplicates, and manage scraper sources and runs
        </p>
      </div>

      {/* General KPIs — compact single-row cards; clicking jumps to the relevant section */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-5">
        {/* Case counts — needs review / approved / rejected in one card */}
        <button
          onClick={() => setTopLevel('validation')}
          className="text-left bg-white dark:bg-gray-800 shadow rounded-lg px-4 py-3 hover:ring-2 hover:ring-primary/40 transition"
        >
          <div className="flex items-center gap-1.5 mb-2">
            <ShieldCheckIcon className="h-4 w-4 text-primary shrink-0" />
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase truncate">Cases</p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <p className="text-2xl font-bold text-yellow-600 leading-none">{caseStats?.byStatus?.FOR_VALIDATION ?? 0}</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">Needs review</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600 leading-none">{caseStats?.byStatus?.APPROVED ?? 0}</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">Approved</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600 leading-none">{caseStats?.byStatus?.REJECTED ?? 0}</p>
              <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">Rejected</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => setTopLevel('duplicates')}
          className="flex items-center justify-between gap-3 text-left bg-white dark:bg-gray-800 shadow rounded-lg px-4 py-3 hover:ring-2 hover:ring-primary/40 transition"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <DocumentDuplicateIcon className="h-4 w-4 text-indigo-500 shrink-0" />
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase truncate">Duplicates to Confirm</p>
            </div>
            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 truncate">Awaiting a decision</p>
          </div>
          <p className="text-2xl font-bold text-indigo-600 shrink-0">{pendingMatches?.length ?? 0}</p>
        </button>

        <button
          onClick={() => setTopLevel('config')}
          className="flex items-center justify-between gap-3 text-left bg-white dark:bg-gray-800 shadow rounded-lg px-4 py-3 hover:ring-2 hover:ring-primary/40 transition"
        >
          <div className="min-w-0">
            <div className="flex items-center gap-1.5">
              <Cog6ToothIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 shrink-0" />
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase truncate">Active Sources</p>
            </div>
            <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-0.5 truncate">{stats ? `of ${stats.sources.total} total · ${stats.sources.errors} errors` : ' '}</p>
          </div>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100 shrink-0">{stats?.sources.enabled ?? 0}</p>
        </button>
      </div>

      {/* Top-level section switcher — full-width segmented pills (distinct from the underline sub-tabs below) */}
      <div className="mb-6">
        <div className="flex w-full gap-1 rounded-lg bg-gray-100 dark:bg-gray-700 p-1">
          {([
            { key: 'validation' as TopLevel, label: 'AI Assisted Verification', Icon: ShieldCheckIcon },
            { key: 'duplicates' as TopLevel, label: 'Duplicate Cases Checking', Icon: DocumentDuplicateIcon },
            { key: 'config' as TopLevel, label: 'Sources and Runs', Icon: Cog6ToothIcon },
          ]).map(({ key, label, Icon }) => (
            <button
              key={key}
              onClick={() => setTopLevel(key)}
              className={`flex-1 px-4 py-2 text-sm font-medium rounded-md transition-colors inline-flex items-center justify-center gap-1.5 ${
                topLevel === key
                  ? 'bg-primary text-white shadow-sm'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700'
              }`}
            >
              <Icon className="h-4 w-4 shrink-0" />
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Config Sub-tabs */}
      {topLevel === 'config' && (
        <div className="border-b border-gray-200 dark:border-gray-700 mb-6">
          <nav className="-mb-px flex gap-6">
            {([{ key: 'sources' as ConfigTab, label: 'Sources' }, { key: 'runs' as ConfigTab, label: 'Runs' }]).map((tab) => (
              <button
                key={tab.key}
                onClick={() => setConfigTab(tab.key)}
                className={`pb-3 text-sm font-medium border-b-2 transition-colors ${
                  configTab === tab.key
                    ? 'border-primary text-primary'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
      )}


      {/* Loading State */}
      {isLoading && (
        <div className="animate-pulse">
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow h-24"></div>
            ))}
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <div className="text-center py-12">
          <p className="text-red-600">Failed to load data</p>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
            {error instanceof Error ? error.message : 'Unknown error'}
          </p>
        </div>
      )}

      {/* Sources Tab */}
      {topLevel === 'config' && configTab === 'sources' && !isLoading && !error && (
        <>
          {/* Toolbar */}
          <div className="mb-6 flex flex-wrap items-center gap-3">
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
            >
              <option value="">All types</option>
              {Object.entries(typeLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>

            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
            >
              <option value="">All categories</option>
              {Object.entries(categoryLabels).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>

            <select
              value={regionFilter}
              onChange={(e) => setRegionFilter(e.target.value)}
              className="rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
            >
              <option value="">All regions</option>
              <option value="LAC">LAC</option>
              <option value="ASIA">Asia</option>
              <option value="AFRICA">Africa</option>
            </select>

            <select
              value={enabledFilter}
              onChange={(e) => setEnabledFilter(e.target.value)}
              className="rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
            >
              <option value="">All</option>
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </select>

            <div className="flex-1" />

            <button
              onClick={openCreate}
              className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
            >
              <PlusIcon className="h-4 w-4" />
              Add Source
            </button>
          </div>

          {/* Sources Table */}
          <div className="bg-white dark:bg-gray-800 shadow sm:rounded-md overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-900">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Source
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Tier
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Items / Max
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Country
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Last Scraped
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Enabled
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {!scraperSources || scraperSources.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center text-gray-500 dark:text-gray-400">
                      No scraper sources configured
                    </td>
                  </tr>
                ) : (
                  scraperSources.map((source) => (
                    <tr key={source.id} className={!source.enabled ? 'opacity-50' : ''}>
                      <td className="px-4 py-3">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{source.name}</div>
                        <div
                          className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs"
                          title={source.url}
                        >
                          {source.url}
                        </div>
                        {source.keywords && source.keywords.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {source.keywords.map((kw) => (
                              <span key={kw} className="inline-flex px-1.5 py-0.5 text-xs rounded bg-indigo-50 text-indigo-600 dark:text-indigo-400">
                                {kw}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                          {typeLabels[source.type] || source.type}
                        </span>
                        <span className="ml-1 text-xs text-gray-400 dark:text-gray-500">
                          {categoryLabels[source.category] || source.category}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${
                            tierLabels[source.tier]?.color || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }`}
                        >
                          {tierLabels[source.tier]?.label || `Tier ${source.tier}`}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {source.lastItemCount != null ? (
                          <span>{source.lastItemCount} <span className="text-gray-400 dark:text-gray-500">/ {source.maxItems}</span></span>
                        ) : (
                          source.maxItems
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                        {source.country || <span className="text-gray-400 dark:text-gray-500">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {source.lastStatus ? (
                          <div className="flex items-center gap-1">
                            {statusIcons[source.lastStatus] || (
                              <span className="h-4 w-4 rounded-full bg-gray-300 inline-block" />
                            )}
                            <span className="text-xs text-gray-600 dark:text-gray-400">{source.lastStatus}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 dark:text-gray-500">Never</span>
                        )}
                        {source.lastError && (
                          <div
                            className="text-xs text-red-500 truncate max-w-[150px]"
                            title={source.lastError}
                          >
                            {source.lastError}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400">
                        {source.lastScrapedAt ? (
                          <span
                            title={format(
                              new Date(source.lastScrapedAt),
                              'MMM d, yyyy h:mm a'
                            )}
                          >
                            {formatDistanceToNow(new Date(source.lastScrapedAt), {
                              addSuffix: true,
                            })}
                          </span>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500">Never</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => toggleEnabled(source)}
                          disabled={updateMutation.isPending}
                          className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                            source.enabled ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-600'
                          }`}
                        >
                          <span
                            className={`inline-block h-4 w-4 transform rounded-full bg-white dark:bg-gray-800 shadow transition duration-200 ease-in-out ${
                              source.enabled ? 'translate-x-4' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openEdit(source)}
                            className="p-1 text-gray-400 dark:text-gray-500 hover:text-primary"
                            title="Edit"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => setDeleteConfirm(source.id)}
                            className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600"
                            title="Delete"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Runs Tab */}
      {topLevel === 'config' && configTab === 'runs' && !isLoading && !error && (
        <>
          {/* Schedule Section */}
          {schedules && schedules.length > 0 &&
            (() => {
              const schedule = schedules[0] as ScraperSchedule;
              const formatHour = (h: number) => {
                if (h === 0) return '12:00 AM';
                if (h === 12) return '12:00 PM';
                return h < 12 ? `${h}:00 AM` : `${h - 12}:00 PM`;
              };
              const runTimes: string[] = [];
              if (schedule.runAtHour !== null) {
                for (let h = schedule.runAtHour; h < 24; h += schedule.intervalHours) {
                  runTimes.push(formatHour(h));
                }
              }

              return (
                <div className="mb-6 bg-white dark:bg-gray-800 shadow sm:rounded-md p-5">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        Automatic Schedule
                      </h2>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Automatically run the scraper on all sources
                      </p>
                    </div>
                    <button
                      onClick={() =>
                        scheduleMutation.mutate({
                          id: schedule.id,
                          data: { enabled: !schedule.enabled },
                        })
                      }
                      disabled={scheduleMutation.isPending}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        schedule.enabled ? 'bg-primary' : 'bg-gray-200 dark:bg-gray-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-5 w-5 transform rounded-full bg-white dark:bg-gray-800 shadow transition duration-200 ease-in-out ${
                          schedule.enabled ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>

                  <div
                    className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${
                      !schedule.enabled ? 'opacity-50 pointer-events-none' : ''
                    }`}
                  >
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Frequency
                      </label>
                      <select
                        value={schedule.intervalHours}
                        onChange={(e) =>
                          scheduleMutation.mutate({
                            id: schedule.id,
                            data: { intervalHours: parseInt(e.target.value, 10) },
                          })
                        }
                        disabled={scheduleMutation.isPending}
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
                      >
                        <option value={4}>Every 4 hours</option>
                        <option value={8}>Every 8 hours</option>
                        <option value={12}>Every 12 hours</option>
                        <option value={24}>Once a day</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Starting at
                      </label>
                      <select
                        value={schedule.runAtHour ?? 0}
                        onChange={(e) =>
                          scheduleMutation.mutate({
                            id: schedule.id,
                            data: { runAtHour: parseInt(e.target.value, 10) },
                          })
                        }
                        disabled={scheduleMutation.isPending}
                        className="w-full rounded-md border border-gray-300 dark:border-gray-600 px-3 py-2 text-sm"
                      >
                        {Array.from({ length: 24 }, (_, i) => (
                          <option key={i} value={i}>
                            {formatHour(i)}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {schedule.enabled && runTimes.length > 0 && (
                    <div className="mt-4 text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-900 rounded-md px-3 py-2">
                      <span className="font-medium">Runs at: </span>
                      {runTimes.join(', ')}
                      <span className="text-gray-400 dark:text-gray-500 ml-1">(UTC)</span>
                    </div>
                  )}
                </div>
              );
            })()}

          {/* Run Scraper */}
          <div className="mb-6 bg-white dark:bg-gray-800 shadow sm:rounded-md p-4">
            <div className="flex flex-wrap items-center gap-3">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mr-2">Run Scraper</h3>
              <select
                value={triggerTier}
                onChange={(e) => setTriggerTier(e.target.value)}
                className="rounded-md border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm"
              >
                <option value="">All tiers</option>
                <option value="1">Tier 1 (4hr)</option>
                <option value="2">Tier 2 (8hr)</option>
                <option value="3">Tier 3 (24hr)</option>
                <option value="4">Tier 4 (twice a week)</option>
                <option value="5">Tier 5 (once a week)</option>
              </select>
              <select
                value={triggerSourceType}
                onChange={(e) => setTriggerSourceType(e.target.value)}
                className="rounded-md border border-gray-300 dark:border-gray-600 px-3 py-1.5 text-sm"
              >
                <option value="">All sources</option>
                {Object.entries(typeLabels).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
              <button
                onClick={() => {
                  const data: { tier?: number; sourceType?: ScraperSourceType } = {};
                  if (triggerTier) data.tier = parseInt(triggerTier, 10);
                  if (triggerSourceType) data.sourceType = triggerSourceType as ScraperSourceType;
                  triggerMutation.mutate(data);
                }}
                disabled={triggerMutation.isPending}
                className="inline-flex items-center gap-1.5 rounded-md bg-green-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50"
              >
                <PlayIcon className="h-4 w-4" />
                {triggerMutation.isPending ? 'Triggering...' : 'Run Now'}
              </button>
            </div>
          </div>

          {/* Recent Runs Table */}
          <div className="bg-white dark:bg-gray-800 shadow sm:rounded-md overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Recent Runs</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">Auto-refreshes every 15 seconds</p>
            </div>

            {!triggers || triggers.length === 0 ? (
              <div className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                No scraper runs yet. Use the controls above to trigger a run.
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Tier
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Source
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Triggered
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Duration
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                      Result
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {triggers.map((t: ScraperTrigger) => {
                    const statusColors: Record<string, string> = {
                      PENDING: 'bg-yellow-100 text-yellow-700',
                      RUNNING: 'bg-blue-100 text-blue-700',
                      COMPLETED: 'bg-green-100 text-green-700',
                      FAILED: 'bg-red-100 text-red-700',
                    };
                    const duration =
                      t.startedAt && t.completedAt
                        ? `${Math.round(
                            (new Date(t.completedAt).getTime() -
                              new Date(t.startedAt).getTime()) /
                              1000
                          )}s`
                        : t.status === 'RUNNING' && t.startedAt
                        ? 'running...'
                        : '-';
                    const result = t.result as Record<string, number> | null;
                    const resultSummary = result
                      ? `${result.casesCreated || 0} cases, ${result.sourcesScraped || 0} sources`
                      : t.error
                      ? t.error.substring(0, 50)
                      : '-';
                    return (
                      <tr key={t.id}>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full ${
                              statusColors[t.status] || 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {t.status === 'RUNNING' && (
                              <ArrowPathIcon className="h-3 w-3 animate-spin" />
                            )}
                            {t.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {t.tier ? `Tier ${t.tier}` : 'all'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                          {t.sourceType ? typeLabels[t.sourceType] || t.sourceType : 'all'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                          {formatDistanceToNow(new Date(t.createdAt), { addSuffix: true })}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">{duration}</td>
                        <td
                          className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 truncate max-w-[250px]"
                          title={resultSummary}
                        >
                          {resultSummary}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* Validation */}
      {topLevel === 'validation' && <ReviewQueueTab />}

      {/* Duplicates */}
      {topLevel === 'duplicates' && <DuplicatesTab />}
    </div>
  );
}
